/**
 * Leads-tavlen (VIB-3050): søylene, slippsonen og hvilke flyttinger som er lov.
 * Å dra et kort er det samme som å trykke tasten på leadkortet.
 */

export type BoardColumn = "to_contact" | "to_email" | "to_call" | "booked";

/** Hvor et kort kan slippes: tre søyler og fire felt i slippsonen. */
export type BoardTarget = BoardColumn | "no_answer" | "rejected" | "wrong_number" | "pool";

/** Hva et lovlig slipp gjør. «dialog» åpner samme dialog som tasten. */
export type BoardAction =
  | { kind: "dialog"; dialog: "email_planned" | "call_back" | "book_meeting" | "rejected" }
  | { kind: "log"; outcome: "no_answer" | "wrong_number" }
  | { kind: "pool" };

export type MoveResult = { ok: true; action: BoardAction } | { ok: false; message: string } | null;

export const BOARD_COLUMNS: { id: BoardColumn; label: string }[] = [
  { id: "to_contact", label: "Leads å kontakte" },
  { id: "to_email", label: "Skal sende mail" },
  { id: "to_call", label: "Skal ringe opp igjen" },
  { id: "booked", label: "Møte booket" },
];

export const DROP_ZONES: { id: Exclude<BoardTarget, BoardColumn>; label: string; key?: string }[] = [
  { id: "no_answer", label: "Ikke svar", key: "1" },
  { id: "rejected", label: "Avvist", key: "5" },
  { id: "wrong_number", label: "Feil nummer", key: "6" },
  { id: "pool", label: "Tilbake til poolen" },
];

/** Tastene på et valgt kort, samme tall som i tabellen i wireframen. */
export const BOARD_KEYS: Record<string, BoardTarget> = {
  "1": "no_answer",
  "2": "to_call",
  "3": "to_email",
  "4": "booked",
  "5": "rejected",
  "6": "wrong_number",
};

/** Hva som skjer når et kort fra `from` slippes på `to`. null = ingenting (samme søyle). */
export function boardMove(from: BoardColumn, to: BoardTarget): MoveResult {
  if (from === to) return null;
  if (from === "booked") return { ok: false, message: "Et booket møte flyttes ikke herfra. Avlys det i Pipe." };
  switch (to) {
    case "to_contact":
      return { ok: false, message: "Ingenting kan dras tilbake hit. Bruk «Tilbake til poolen»." };
    case "to_email":
      return { ok: true, action: { kind: "dialog", dialog: "email_planned" } };
    case "to_call":
      return { ok: true, action: { kind: "dialog", dialog: "call_back" } };
    case "booked":
      return { ok: true, action: { kind: "dialog", dialog: "book_meeting" } };
    case "rejected":
      return { ok: true, action: { kind: "dialog", dialog: "rejected" } };
    case "no_answer":
    case "wrong_number":
      return { ok: true, action: { kind: "log", outcome: to } };
    case "pool":
      return from === "to_contact"
        ? { ok: false, message: "Leadet ligger allerede i køen din." }
        : { ok: true, action: { kind: "pool" } };
  }
}

/** Forfalt: e-postdatoen er før i dag, eller ringetidspunktet har passert. */
export function isOverdue(column: BoardColumn, dueAt: string | null, now: Date = new Date()): boolean {
  if (!dueAt) return false;
  if (column === "to_email") {
    const pad = (n: number) => String(n).padStart(2, "0");
    const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    return dueAt.slice(0, 10) < today;
  }
  if (column === "to_call") return new Date(dueAt).getTime() < now.getTime();
  return false;
}
